import React, { useContext, useEffect } from 'react';
import Head from 'next/head';
import deepEqual from 'deep-equal';

import { ImageFieldValue } from '@sitecore-jss/sitecore-jss-manifest';
import { getFieldValue, getPublicUrl, Placeholder, withSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';
import { WildCardRouteContext } from 'src/context/WildCardRoute/WildCardRouteContext';

import { SitecoreContextValue } from 'lib/ComponentProps';

import { favouritesRequest } from 'src/services/favourites/favouritesRequest';

import { AuthenticatedUserProfile } from 'src/types/AuthenticatedUserProfile';
import { SeoMetaData } from 'src/types/SeoMetaData';

import IsSitecoreEditing from 'src/utils/isSitecoreEditing';

import { Clarity } from 'src/components/global/_Clarity/_Clarity';
import { GoogleTag } from 'src/components/global/_GoogleTag/_GoogleTag';
import GTVirtualPageView from 'src/components/global/_GoogleTag/_GTVirtualPageView';
import GaScripts from 'src/components/global/_Gtm/_GaScripts';
import { GtmTag } from 'src/components/global/_Gtm/_GtmTag';
import { Hotjar } from 'src/components/global/_Hotjar/_Hotjar';
import OneTrust from 'src/components/global/_OneTrust/OneTrust';

// Prefix public assets with a public URL to enable compatibility with Sitecore Experience Editor.
// If you're not supporting the Experience Editor, you can remove this.
const publicUrl = getPublicUrl();

type LayoutProps = {
	sitecoreContext: SitecoreContextValue & {
		authenticatedUserProfile?: AuthenticatedUserProfile;
		wildcardMetaData?: SeoMetaData;
	};
};

export default withSitecoreContext()(React.memo(Layout, propsAreEqual));

function Layout(props: LayoutProps): JSX.Element {
	const RcnUser = useContext(RcnUserContext);
	const WildCardRoute = useContext(WildCardRouteContext);

	useEffect(() => {
		if (props.sitecoreContext.authenticatedUserProfile) {
			RcnUser.updateAuthenticatedUserProfile(props.sitecoreContext.authenticatedUserProfile);
		}
	}, [props.sitecoreContext.authenticatedUserProfile]);

	useEffect(() => {
		if (RcnUser.authenticatedUserProfile) {
			(async () => {
				const { data } = await favouritesRequest();
				RcnUser.updateUserFavourites(data);
			})();
		}
	}, [RcnUser.authenticatedUserProfile]);

	useEffect(() => {
		if (props.sitecoreContext?.wildcardMetaData) {
			WildCardRoute.updateWildCardItemInfo({
				Id: props.sitecoreContext?.wildcardMetaData?.pageID,
				TemplateId: props.sitecoreContext?.wildcardMetaData?.pageTemplateID,
				Title: props.sitecoreContext?.wildcardMetaData?.pageTitle,
			});
		} else {
			WildCardRoute.updateWildCardItemInfo(undefined);
		}
	}, [props.sitecoreContext.wildcardMetaData]);

	const isEditing = IsSitecoreEditing();

	const fields = props.sitecoreContext.route.fields;

	if (!fields) {
		return <p>Error: sitecoreContext.route.fields undefined</p>;
	}

	const metaTags = () => {
		let metaTitle = getFieldValue<string>(fields, 'MetaTitle');
		let metaDescription = getFieldValue<string>(fields, 'MetaDescription')
			? getFieldValue<string>(fields, 'MetaDescription')
			: getFieldValue<string>(fields, 'Strapline');
		let metaKeywords = getFieldValue<string>(fields, 'MetaKeywords');
		let metaCanonical = getFieldValue<string>(fields, 'MetaCanonical');
		let ogMetaTitle = getFieldValue<string>(fields, 'OGMetaTitle');
		let ogDescription = getFieldValue<string>(fields, 'OGMetaDescription')
			? getFieldValue<string>(fields, 'OGMetaDescription')
			: metaDescription;
		const image = getFieldValue<ImageFieldValue | null>(fields, 'OGMetaImage');
		let ogImage = image?.src || '';
		let ogMetaType = getFieldValue<string>(fields, 'OGMetaType');
		let ogMetaUrl = getFieldValue<string>(fields, 'OGMetaUrl');
		let accessTypes = '';
		let ogMetaDoNotIndex = getFieldValue<boolean>(fields, 'MetaDoNotIndex');

		if (props.sitecoreContext.wildcardMetaData) {
			metaTitle = props.sitecoreContext.wildcardMetaData.metaTitle;
			metaDescription = props.sitecoreContext.wildcardMetaData.metaDescription;
			metaKeywords = props.sitecoreContext.wildcardMetaData.metaKeywords;
			metaCanonical = props.sitecoreContext.wildcardMetaData.metaCanonical;
			ogMetaTitle = props.sitecoreContext.wildcardMetaData.ogMetaTitle ?? props.sitecoreContext.wildcardMetaData.metaTitle;
			ogDescription = props.sitecoreContext.wildcardMetaData.ogDescription ?? props.sitecoreContext.wildcardMetaData.metaDescription;
			ogImage = props.sitecoreContext.wildcardMetaData.ogMetaImage;
			ogMetaType = props.sitecoreContext.wildcardMetaData.ogMetaType;
			ogMetaUrl = props.sitecoreContext.wildcardMetaData.ogMetaUrl;
			accessTypes = props.sitecoreContext.wildcardMetaData.accessTypes;
			ogMetaDoNotIndex = props.sitecoreContext.wildcardMetaData.metaDoNotIndex;
		}

		return (
			<>
				<title>{metaTitle}</title>
				<meta name="referrer" content="no-referrer-when-downgrade" />
				<meta name="robots" content={ogMetaDoNotIndex ? 'noindex, follow' : process.env.NEXT_PUBLIC_DO_NOT_INDEX} />
				<meta name="description" content={metaDescription} />
				<meta name="keywords" content={metaKeywords} />
				<meta name="canonical" content={metaCanonical} />
				<meta name="og:title" content={ogMetaTitle} />
				<meta name="og:description" content={ogDescription} />
				<meta name="og:image" content={ogImage} />
				<meta name="og:type" content={ogMetaType} />
				<meta name="og:url" content={ogMetaUrl} />
				<meta name="viewport" content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=0" />
				<link rel="icon" type="image/png" sizes="any" href={`${publicUrl}/favicon/favicon.png`} />
				<link rel="icon" type="image/svg+xml" href={`${publicUrl}/favicon/favicon.svg`} />
				<link rel="apple-touch-icon" type="image/png" href={`${publicUrl}/favicon/favicon_white_background.png`} />
				<link rel="icon" type="image/png" sizes="192x192" href={`${publicUrl}/favicon/favicon-192.png`} />
				{accessTypes && <meta name="accesstype" content={accessTypes} />}
			</>
		);
	};

	return (
		<>
			<Head>{metaTags()}</Head>

			{!isEditing && (
				<>
					<GoogleTag />
					<GtmTag />
					<Hotjar />
					<Clarity />
					<OneTrust />
					<GaScripts
						MembershipNumber={RcnUser.authenticatedUserProfile?.hashedMembershipNumber}
						EmployerSector={RcnUser.authenticatedUserProfile?.contact?.employer?.sectorTypeLabel}
						Branch={RcnUser.authenticatedUserProfile?.contact?.branch?.name}
						Region={RcnUser.authenticatedUserProfile?.contact?.region?.name}
						CategoryName={RcnUser.authenticatedUserProfile?.contracts?.membership?.description}
					/>
					<GTVirtualPageView sitecoreContext={props.sitecoreContext} wildcardMetaData={props.sitecoreContext.wildcardMetaData} />
				</>
			)}

			<Placeholder name="rcnlearn-header" rendering={props.sitecoreContext.route} />
			<main>
				<Placeholder name="rcnlearn-main" rendering={props.sitecoreContext.route} />
			</main>
			<Placeholder name="rcnlearn-footer" rendering={props.sitecoreContext.route} />
		</>
	);
}

function propsAreEqual(prevProps: LayoutProps, nextProps: LayoutProps) {
	if (deepEqual(prevProps.sitecoreContext.route, nextProps.sitecoreContext.route)) {
		return true;
	}

	return false;
}
